import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { KpiDrillPanel } from "../../components/dashboard/KpiDrill";
import { UserMinus } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { dateBR, dateTimeBR } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, Card, Loading } from "../../components/common";
import { Button } from "../../components/ui/button";
import { NewRequestDialog } from "../../components/NewRequestDialog";
import { RequestDetailSheet } from "../../components/RequestDetailSheet";
import { refTitle } from "../../components/dashboard/CompetenciaBar";
import { ProcessFilters, applyFilters, Pager, EMPTY } from "../../components/filters/ProcessFilters";

const SIZE = 20;
const acc = (r) => ({ employee: r.employee_id, date: r.date, requested: r.requested_at, status: r.status, protocol: r.protocol, ref: r.ref });
const uniq = (xs) => [...new Map(xs.map((x) => [x.id, x])).values()].sort((a, b) => a.name.localeCompare(b.name));

export default function Dismissals() {
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [sp] = useSearchParams();
  const [f, setF] = useState(() => (sp.get("kpi") && sp.get("ref") ? { ...EMPTY, ref: sp.get("ref") } : EMPTY));
  const [page, setPage] = useState(1);
  const [neu, setNeu] = useState(false);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/dismissals").then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  if (err) return <Card className="p-8 text-center text-sm text-rose-700">{err}</Card>;
  if (!d) return <Loading />;
  const rows = applyFilters(d.rows, f, acc);
  const shown = rows.slice((page - 1) * SIZE, page * SIZE);
  const refs = [...new Set(d.rows.map((r) => r.ref).filter(Boolean))].sort().reverse();
  return (
    <div className="space-y-5" data-testid="dismissals-page">
      <KpiDrillPanel cards={["dis"]} />
      <PageHeader eyebrow="Desligamentos" title="Desligamentos da equipe" subtitle="Acompanhe os desligamentos solicitados ao DPdigi, com datas, protocolo e status."
        actions={can("requests.create") && <Button onClick={() => setNeu(true)} data-testid="dismissals-new"><UserMinus className="mr-1.5 h-4 w-4" />+ NOVO DESLIGAMENTO</Button>} />
      <div className="flex flex-wrap items-center gap-2">
        <ProcessFilters value={f} onApply={(v) => { setF(v); setPage(1); }} employees={uniq(d.rows.map((r) => ({ id: r.employee_id, name: r.name })))} refs={refs} statuses={d.statuses} testid="dismissals-filters" />
        <span className="ml-auto text-xs text-slate-500" data-testid="dismissals-count">{rows.length} resultado(s)</span>
      </div>
      <Card className="overflow-x-auto">
        {!shown.length ? <p className="p-10 text-center text-sm text-slate-500" data-testid="dismissals-empty">Nenhum desligamento para os filtros selecionados.</p> : (
          <table className="w-full text-sm" data-testid="dismissals-table">
            <thead className="bg-slate-50 text-xs text-slate-500"><tr>{["Funcionário", "Data do desligamento", "Competência", "Tipo", "Protocolo", "Status", "Solicitado em", ""].map((h) => <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>)}</tr></thead>
            <tbody>{shown.map((r) => (
              <tr key={r.id} className="border-t" data-testid={`dismissal-row-${r.id}`}>
                <td className="px-4 py-2.5 font-medium"><Link to={`/empresa/funcionarios/${r.employee_id}`} className="hover:underline">{r.name}</Link></td>
                <td className="px-4 text-xs whitespace-nowrap">{r.date ? dateBR(r.date) : "—"} <span className={`ml-1 rounded px-1 text-[10px] font-semibold ${r.date_kind === "efetiva" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{r.date_kind}</span></td>
                <td className="px-4 text-xs">{r.ref ? refTitle(r.ref) : "—"}</td>
                <td className="px-4 text-xs">{r.reason || "—"}{r.notice ? ` · aviso ${r.notice.toLowerCase()}` : ""}</td>
                <td className="px-4 font-mono text-xs">{r.protocol}</td>
                <td className="px-4 text-xs" data-testid={`dismissal-status-${r.id}`}>{r.status_label}</td>
                <td className="px-4 text-xs whitespace-nowrap">{dateTimeBR(r.requested_at)}</td>
                <td className="px-4"><Button size="sm" variant="outline" onClick={() => setOpen(r.id)} data-testid={`dismissal-open-${r.id}`}>Acompanhar</Button></td>
              </tr>))}</tbody>
          </table>)}
      </Card>
      <Pager page={page} total={rows.length} size={SIZE} onPage={setPage} testid="dismissals-pager" />
      <NewRequestDialog open={neu} type="desligamento" onOpenChange={(o) => { setNeu(o); if (!o) load(); }} onCreated={load} />
      <RequestDetailSheet id={open} open={!!open} onOpenChange={(o) => !o && setOpen(null)} onChanged={load} />
    </div>);
}
